/**
 * useKiaanStatus — is Kiaan switched on for this person on this server?
 *
 * Reads GET /api/v1/kiaan/status (kiaan-live.ts): { enabled, readOnly, reason? }. The server decides
 * from KIAAN_ENABLED and KIAAN_PROFILES; the client only hides. Fails closed: while loading, on a
 * network error, on 401/403/503, Kiaan is treated as OFF and AppShell keeps the old AI panel.
 *
 * Cached in module scope per signed-in user for the session (one request per login); a failure is
 * not remembered, the next mount asks again.
 */
import { useEffect, useState } from 'react';
import { kiaanHttp } from './kiaanApi';
import { useAuthStore } from '../../stores/auth';

export interface KiaanStatus {
  enabled: boolean;
  readOnly: boolean;
  reason?: 'off' | 'profile';
}

const OFF: KiaanStatus = Object.freeze({ enabled: false, readOnly: false, reason: 'off' }) as KiaanStatus;

let cacheKey: string | null = null;
let cached: Promise<{ ok: boolean; status: KiaanStatus }> | null = null;

async function fetchStatus(): Promise<{ ok: boolean; status: KiaanStatus }> {
  try {
    const res = await kiaanHttp.get('/status');
    const d = (res?.data as { data?: unknown } | undefined)?.data;
    if (!d || typeof d !== 'object' || typeof (d as { enabled?: unknown }).enabled !== 'boolean') return { ok: false, status: OFF };
    const s = d as KiaanStatus;
    return { ok: true, status: { enabled: s.enabled === true, readOnly: s.readOnly === true, ...(s.reason ? { reason: s.reason } : {}) } };
  } catch {
    return { ok: false, status: OFF }; // fail closed
  }
}

function load(userId: string): Promise<{ ok: boolean; status: KiaanStatus }> {
  if (cacheKey === userId && cached) return cached;
  cacheKey = userId;
  const p = fetchStatus();
  cached = p;
  void p.then((r) => { if (!r.ok && cached === p) cached = null; });
  return p;
}

/** Tests and a re-login: forget the answer. */
export function resetKiaanStatusCache(): void {
  cacheKey = null;
  cached = null;
}

export function useKiaanStatus(): KiaanStatus & { loading: boolean } {
  const userId = useAuthStore((s) => s.user?.id ?? null);
  const [status, setStatus] = useState<KiaanStatus>(OFF);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    let alive = true;
    if (!userId) { setStatus(OFF); setLoading(false); return; }
    setLoading(true);
    load(userId).then((r) => {
      if (!alive) return;
      setStatus(r.status);
      setLoading(false);
    });
    return () => { alive = false; };
  }, [userId]);
  return { ...status, loading };
}
